<script>
    import Paper from "@smui/paper";
    import Flex from "svelte-flex";
    import Button from "@smui/button";
    import Select, { Option } from "@smui/select";
    import Checkbox from "@smui/checkbox";
    import Textfield from "@smui/textfield";
    import { _ } from "svelte-i18n";

    import { getLoggingConfig as apiGetLoggingConfig, updateLoggingConfig as apiUpdateLoggingConfig } from "../../../../api/LoggingConfig.js";
    import { handleApiError } from "../../lib/errorHandler";
    import { snackbarSuccess, snackbarError, snackbarWarning, authenticatedUser } from "../../../../stores/AppStatus";
    import FullscreenLoader from "../../../elementary/FullscreenLoader.svelte";

    import ConfirmationDialog from "../../../dialogs/ConfirmationDialog.svelte";

    let loaded = false;
    let showConfirmationDialog = false;
    let loggingConfigData = {"enabled": false, "level": "DEBUG", "maxBodyLength": 1000000, "persistRuntimeChanges": false};
    let loggingLevels = ['ERROR','BASIC','FULL','DEBUG'];

    let loadingData = false;
    let loadingDataMessage = $_("processing");

    $: isMaxBodyLengthInvalid =
        loggingConfigData.maxBodyLength === null ||
        loggingConfigData.maxBodyLength === "" ||
        Number(loggingConfigData.maxBodyLength) < 0 ||
        Number(loggingConfigData.maxBodyLength) > 10000000;

    $: isLoggingConfigInvalid =
        !loggingConfigData.level ||
        isMaxBodyLengthInvalid;

    async function loadLoggingConfiguration() {
        console.log("================ loadLoggingConfiguration ================");
        loadingData = true;
        loadingDataMessage = $_("loading_logging_configuration");

        await
        apiGetLoggingConfig()
        .then((res) => {
            if (res.length == 1) {
                loggingConfigData.enabled = res[0].enabled;
                loggingConfigData.level = res[0].level;
                loggingConfigData.maxBodyLength = res[0].maxBodyLength;
                loggingConfigData.persistRuntimeChanges = res[0].persistRuntimeChanges;
                loaded = true;
            };
            loadingData = false;
        })
        .catch((error) => {
            loadingData = false;
            console.error("Error fetching data:", error);
            $snackbarError.text = $_("load_logging_configuration_failed", { values: { reason: error.message } });
            $snackbarError.element && $snackbarError.element.open();
        });
    
    }

    async function saveLoggingConfiguration() {
        if (isLoggingConfigInvalid) {
            return;
        }

        console.log("================ saveLoggingConfiguration ================", loggingConfigData);
        loadingData = true;
        apiUpdateLoggingConfig(loggingConfigData)
            .then((res) => {
                console.log({ res });
                $snackbarSuccess.text = $_("updated");
                $snackbarSuccess.element && $snackbarSuccess.element.open();
                loadingData = false;
            })
            .catch((error) => {
                loadingData = false;
                console.log(error);
                if (!handleApiError($_("update_failed", { values: { reason: error.message } }), error, "non-fatal")) throw error;
            });
}

</script>

<section class="w-100 header tescoblue-border-bottom-thin sticky-week-header">
    <Flex direction="row" class="grey lighten-3 tescoblue-text w-100 bs-bb pr-4 gap-1 pt-3 pb-3 pl-3 pr-3" justify="between">
        <Flex direction="row" justify="between" class="gap-1 w-100">
            <div class="w-100 font-weight-bold">
                {$_("logging_configuration")}
            </div>
        </Flex>
    </Flex>
</section>

<section class="data w-100">
    <Paper class="grey lighten-3 w-100 bs-bb pt-3">
        <div class="smui-paper__content bs-bb">
            <Flex direction="column" class="mb-5">
                <Button class="tescoblue-text white-tescoblue-outlined pr-4 nowrap" title={$_("load_logging_configuration")} on:click={loadLoggingConfiguration}>
                    {$_("load_logging_configuration")}
                </Button>
            </Flex>
        {#if loaded}
                <Flex direction="column" class="">
                    <table>
                        <tr>
                            <td>{$_("logging_enabled")}:</td>
                            <td>
                                <Checkbox bind:checked={loggingConfigData.enabled} />
                            </td>
                        </tr>
                        <tr>
                            <td>{$_("logging_level")}:</td>
                            <td>
                                <Select
                                    bind:value={loggingConfigData.level}
                                    key={(option) => `${option ? option : ""}`}
                                    variant="outlined"
                                    class="small white black-text"
                                    style="width: 20rem"
                                    placeholder={$_("select_list")}
                                    required
                                    disabled={!loggingConfigData.enabled}
                                    invalid={!loggingConfigData.level}
                                >
                                    {#each loggingLevels as option}
                                        <Option value={option}>{$_("log_level_"+option.toLocaleLowerCase())}</Option>
                                    {/each}
                                </Select>
                            </td>
                        </tr>
                        <tr>
                            <td>{$_("max_response_length")}:</td>
                            <td>
                                <Textfield
                                    type="number"
                                    min="0"
                                    max="10000000"
                                    bind:value={loggingConfigData.maxBodyLength}
                                    class="small white pl-3"
                                    style="width: 20rem"
                                    placeholder={$_("enter_max_response_length")}
                                    required
                                    invalid={isMaxBodyLengthInvalid}
                                    disabled={!loggingConfigData.enabled}
                                />    
                            </td>
                        </tr>
                        <tr>
                            <td>{$_("persist_logging")}:</td>
                            <td>
                                <Checkbox 
                                    disabled={!loggingConfigData.enabled}
                                    bind:checked={loggingConfigData.persistRuntimeChanges} 
                                />
                            </td>
                        </tr>
                    </table>
                </Flex>
                <Flex direction="column" class="mt-5">
                    <Button
                        class={isLoggingConfigInvalid? "grey-text white-grey-outlined pr-4 nowrap": "tescoblue-text white-tescoblue-outlined pr-4 nowrap"}
                        title={$_("set_logging_configuration")}
                        disabled={isLoggingConfigInvalid}
                        on:click={() => {showConfirmationDialog=true;}}
                    >
                        {$_("set_logging_configuration")}
                    </Button>
                </Flex>
            {:else}
                <Flex direction="column" class="w-100 pt-4">
                    <div class="tescoblue-text fs-1rem">
                        {$_("press_button_to_load_logging_config")}
                    </div>
                </Flex>
            {/if}
        </div>
    </Paper>
</section>

<FullscreenLoader open={loadingData} text={loadingDataMessage} />

<ConfirmationDialog
    open={showConfirmationDialog}
    title={$_("set_logging_configuration")}
    noBtnText={$_("no")}
    yesBtnText={$_("yes")}
    big={false}
    itemData={null}
    headerColors="tescoblue white-text"
    on:reject={() => {
        showConfirmationDialog = false;
    }}
    on:confirm={() => {
        showConfirmationDialog = false;
        saveLoggingConfiguration();
    }}
>
    {$_("do_you_want_to_change_logging_configuration")}
</ConfirmationDialog>
